import { ReactNode } from "react";

interface FormFieldProps {
  /** Field label */
  label: string;
  /** Field ID for label association */
  id: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Helper text to display below the field */
  helperText?: string;
  /** Optional field indicator (shows as muted styling) */
  optional?: boolean;
  /** Child input component */
  children: ReactNode;
  /** Additional CSS class for the field wrapper */
  className?: string;
}

/**
 * FormField provides consistent styling and structure for form fields.
 * Handles labels, required indicators, error messages, and helper text.
 */
export function FormField({
  label,
  id,
  required = false,
  error,
  helperText,
  optional = false,
  children,
  className = "",
}: FormFieldProps) {
  return (
    <div
      className={`field ${className}`.trim()}
      style={{ marginBottom: "0.25rem" }}
    >
      <label
        htmlFor={id}
        className={`block mb-1 ${error ? "text-red-700" : ""}`}
        style={{ color: error ? "#dc3545" : "#212529", fontWeight: "600" }}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {optional && !required && (
          <span className="text-gray-500 ml-1 text-sm font-normal">
            (optional)
          </span>
        )}
      </label>
      <div className={error ? "p-invalid" : ""}>{children}</div>
      {error && (
        <small
          className="p-error"
          style={{ color: "#dc3545", display: "block", marginTop: "0.25rem" }}
        >
          {error}
        </small>
      )}
      {helperText && !error && (
        <small
          className="text-gray-500"
          style={{ display: "block", marginTop: "0.25rem" }}
        >
          {helperText}
        </small>
      )}
    </div>
  );
}
